import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Plus, Trash2, Calendar, MapPin, Users, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { addDays, addMonths, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, endOfMonth, endOfWeek, parseISO, isValid } from "date-fns";
import { it } from "date-fns/locale";

const TYPE_COLORS = {
  sopralluogo: "bg-blue-100 text-blue-800 border-blue-300",
  riunione: "bg-purple-100 text-purple-800 border-purple-300",
  cantiere: "bg-amber-100 text-amber-800 border-amber-300",
  scadenza: "bg-red-100 text-red-800 border-red-300",
  altro: "bg-neutral-100 text-neutral-800 border-neutral-300",
};

const safeParse = (iso) => { try { const d = parseISO(iso); return isValid(d) ? d : null; } catch { return null; } };

export default function Agenda() {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month"); // month | week
  const [appts, setAppts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [m, a, c, p] = await Promise.all([
      api.get("/appointments/meta"),
      api.get("/appointments"),
      api.get("/clients"),
      api.get("/projects"),
    ]);
    setMeta(m.data); setAppts(a.data); setClients(c.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
      const out = [];
      let d = start;
      while (d <= end) { out.push(d); d = addDays(d, 1); }
      return out;
    }
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [cursor, view]);

  const apptsByDay = useMemo(() => {
    const map = {};
    appts.forEach((a) => {
      const d = safeParse(a.start);
      if (!d) return;
      const key = format(d, "yyyy-MM-dd");
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }, [appts]);

  const openNew = (date) => {
    const start = date ? new Date(date) : new Date();
    start.setHours(9, 0, 0, 0);
    setEditing({
      title: "",
      type: "sopralluogo",
      start: start.toISOString(),
      location: "",
      description: "",
      client_id: null,
      project_id: null,
    });
    setEditOpen(true);
  };
  const openEdit = (a) => { setEditing({ ...a }); setEditOpen(true); };

  const save = async () => {
    if (!editing.title?.trim() || !editing.start) { toast.error("Titolo e data obbligatori"); return; }
    try {
      if (editing.id) await api.put(`/appointments/${editing.id}`, editing);
      else await api.post("/appointments", editing);
      toast.success("Salvato");
      setEditOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/appointments/${id}`); await load(); };

  if (!meta) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Pianificazione</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Agenda</h1>
          <p className="text-sm text-neutral-500 mt-2">Sopralluoghi, riunioni, cantieri e scadenze dello studio</p>
        </div>
        <Button onClick={() => openNew()} data-testid="new-appt-btn" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" /> Nuovo evento</Button>
      </header>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-2 bg-white border border-neutral-200 p-2">
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(view === "month" ? addMonths(cursor, -1) : addDays(cursor, -7))} className="p-1.5 hover:bg-neutral-100" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-1 mono-label hover:bg-neutral-100" data-testid="cal-today">OGGI</button>
          <button onClick={() => setCursor(view === "month" ? addMonths(cursor, 1) : addDays(cursor, 7))} className="p-1.5 hover:bg-neutral-100" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="font-display text-xl font-medium capitalize">
          {view === "month" ? format(cursor, "MMMM yyyy", { locale: it }) : `${format(days[0], "d MMM")} – ${format(days[6], "d MMM yyyy", { locale: it })}`}
        </div>
        <div className="flex items-center gap-1 border border-neutral-200">
          <button onClick={() => setView("month")} data-testid="cal-view-month" className={`px-3 py-1 text-xs ${view === "month" ? "brand-bg text-white" : "hover:bg-neutral-50"}`}>Mese</button>
          <button onClick={() => setView("week")} data-testid="cal-view-week" className={`px-3 py-1 text-xs ${view === "week" ? "brand-bg text-white" : "hover:bg-neutral-50"}`}>Settimana</button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="bg-white border border-neutral-200" data-testid="cal-grid">
        <div className="grid grid-cols-7 border-b border-neutral-200 bg-neutral-50">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
            <div key={d} className="p-2 mono-label text-center">{d}</div>
          ))}
        </div>
        <div className={`grid grid-cols-7 ${view === "month" ? "" : "min-h-96"}`}>
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const list = apptsByDay[key] || [];
            const inMonth = view === "week" || isSameMonth(d, cursor);
            const isToday = isSameDay(d, new Date());
            return (
              <div
                key={key}
                onClick={() => openNew(d)}
                data-testid={`day-${key}`}
                className={`border-r border-b border-neutral-100 p-1.5 min-h-24 cursor-pointer hover:bg-neutral-50 ${!inMonth ? "bg-neutral-50/50 text-neutral-400" : ""}`}
              >
                <div className={`font-mono text-xs ${isToday ? "brand-text font-bold" : ""}`}>{format(d, "d")}</div>
                <div className="mt-1 space-y-1">
                  {list.slice(0, view === "week" ? 999 : 3).map((a) => {
                    const dt = safeParse(a.start);
                    return (
                      <button
                        key={a.id}
                        onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                        data-testid={`appt-${a.id}`}
                        className={`w-full text-left px-1.5 py-0.5 border font-mono text-[10px] truncate ${TYPE_COLORS[a.type] || TYPE_COLORS.altro}`}
                        title={`${dt ? format(dt, "HH:mm") : ""} · ${a.title}`}
                      >
                        {dt && !a.all_day && <span className="font-semibold">{format(dt, "HH:mm")}</span>} {a.title}
                      </button>
                    );
                  })}
                  {view === "month" && list.length > 3 && <div className="mono-label text-[9px]">+{list.length - 3} altri</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica evento" : "Nuovo evento"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="mono-label">Titolo</label>
                <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="appt-title" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Tipo</label>
                  <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="appt-type" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                    <input type="checkbox" checked={!!editing.all_day} onChange={(e) => setEditing({ ...editing, all_day: e.target.checked })} />
                    Tutto il giorno
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label flex items-center gap-1"><Clock className="h-3 w-3" /> Inizio</label>
                  <Input type="datetime-local" value={editing.start ? editing.start.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, start: new Date(e.target.value).toISOString() })} data-testid="appt-start" />
                </div>
                <div>
                  <label className="mono-label">Fine (opz.)</label>
                  <Input type="datetime-local" value={editing.end ? editing.end.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, end: e.target.value ? new Date(e.target.value).toISOString() : null })} />
                </div>
              </div>
              <div>
                <label className="mono-label flex items-center gap-1"><MapPin className="h-3 w-3" /> Luogo</label>
                <Input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="Via Roma 10, Torino" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Cliente</label>
                  <select value={editing.client_id || ""} onChange={(e) => setEditing({ ...editing, client_id: e.target.value || null })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">— nessuno —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Progetto</label>
                  <select value={editing.project_id || ""} onChange={(e) => setEditing({ ...editing, project_id: e.target.value || null })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">— nessuno —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mono-label">Descrizione</label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            {editing?.id && <Button variant="outline" onClick={() => remove(editing.id)} data-testid="appt-delete" className="mr-auto text-red-600"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Elimina</Button>}
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="appt-save" className="brand-bg hover:opacity-90">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
